import { useQuery } from "@apollo/client";
import "pure-react-carousel/dist/react-carousel.es.css";
import {
  Anchor,
  AnchorButton,
  Box,
  Flex,
  Heading,
  Image,
  LoadingSpinner,
  PageGridContainer,
  SimpleGrid,
  Stack,
  Text,
  getSecureRedirect,
  palette,
} from "@govlaunch/web";
import { CarouselProvider, Slide, Slider, Image as SlideImage, Dot } from "pure-react-carousel";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import getVimeoId from "~/lib/utils/getVimeoId";
import stringOrFirst from "~/src/utils/stringOrFirst";
import NavbarLayout from "~/src/components/navbar/NavbarLayout";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { FormattedMessage, useIntl } from "react-intl";
import GetActiveAboutPageConfig from "~/src/components/home/pages/graphql/GetActiveAboutPageConfig";
import {
  IGetActiveAboutPageConfigQuery,
  IGetActiveAboutPageConfigQueryVariables,
} from "./graphql/__generated__/GetActiveAboutPageConfig.generated";
import { IAboutPageConfigFieldsFragment } from "./graphql/fragments/__generated__/AboutPageConfigFields.generated";
import { IAboutContentType } from "~/types/types";

export default function AboutPage() {
  const router = useRouter();
  const configId = stringOrFirst(router.query.previewId);

  const { data, loading } = useQuery<IGetActiveAboutPageConfigQuery, IGetActiveAboutPageConfigQueryVariables>(
    GetActiveAboutPageConfig,
    {
      variables: {
        configId: configId || null,
      },
      fetchPolicy: "network-only",
    },
  );

  return (
    <NavbarLayout
      pb={0}
      navbarProps={{
        mb: 0,
      }}
      showBanners={false}
    >
      {loading || !data ? (
        <Flex justify="center">
          <LoadingSpinner />
        </Flex>
      ) : (
        <InnerAboutPage {...data?.getActiveAboutPageConfig.config} />
      )}
    </NavbarLayout>
  );
}

function InnerAboutPage({
  bannerBackgroundSrc,
  bannerBackgroundDescription,
  bannerContentType,
  bannerContentText,
  bannerContentSize,
  bannerContentDescription,
  headingText,
  headingTextFontColor,
  subtitleText,
  subtitleTextFontColor,
  leftButtonHref,
  leftButtonText,
  leftButtonBackgroundColor,
  leftButtonHoverColor,
  leftButtonBorderColor,
  leftButtonTextColor,
  rightButtonHref,
  rightButtonBackgroundColor,
  rightButtonHoverColor,
  rightButtonBorderColor,
  rightButtonTextColor,
  rightButtonText,
  boxesSectionBackgroundColor,
  leftBox,
  rightBox,
  videoSectionContentType,
  videoSectionContentText,
  videoSectionBackground,
  videoSectionFontColor,
  videoSectionTitle,
  joinSectionBackground,
  joinSectionTitle,
  joinSectionTitleFontColor,
  joinSectionSubtitle,
  joinSectionSubtitleFontColor,
  joinSectionButtonText,
  joinSectionButtonHref,
  joinSectionButtonFontColor,
  joinSectionButtonColor,
  logoSectionBackgroundColor,
  logoUrls,
}: IAboutPageConfigFieldsFragment["config"]) {
  const intl = useIntl();
  const locale = { id: intl.locale || intl.defaultLocale };

  return (
    <>
      <Box position="relative" pt={[8, 8, "100px"]} pb={["30px", "30px", "30px", "80px"]}>
        {bannerBackgroundSrc && (
          <Image
            ignoreFallback={true}
            alt={bannerBackgroundDescription || ""}
            src={bannerBackgroundSrc}
            // height={["420px", "420px", "520px"]}
            width="100%"
            height="100%"
            objectFit="cover"
            position="absolute"
            left={0}
            top={0}
          />
        )}

        <PageGridContainer paddingLeft="0.5rem" paddingRight="0.5rem">
          <Box
            backgroundColor="red"
            position="relative"
            textAlign={["center", "center", "center", "left"]}
            minHeight="350px"
            display="flex"
            flexDirection={["column", "column", "column", "row"]}
            alignItems={["center", "center", "center", "flex-start"]}
          >
            <Box position="relative" zIndex={99} maxWidth={["100%", "100%", "100%", "50%"]}>
              <Heading
                as="h1"
                fontSize={["32px", "32px", "55px"]}
                color={headingTextFontColor}
                maxWidth={["auto", "auto", "auto", "770px"]}
                lineHeight={["38px", "38px", "60px"]}
              >
                {locale.id === "en-US" ? (
                  headingText
                ) : (
                  <FormattedMessage defaultMessage="The free wiki for local government innovation" id="5yQsxL" />
                )}
                <sup
                  css={{
                    margin: 0,
                    padding: 0,
                    fontSize: 32,
                    "@media (max-width: 768px)": {
                      top: "-0.25em",
                    },
                  }}
                >
                  ®
                </sup>
              </Heading>
              <Heading
                mt={[4, 4, 4, 2]}
                as="h2"
                fontWeight={["bold", "bold", "normal"]}
                fontSize={["18px", "18px", "28px"]}
                color={subtitleTextFontColor}
                maxWidth={["auto", "auto", "auto", "610px"]}
              >
                {locale.id === "en-US" ? (
                  subtitleText
                ) : (
                  <FormattedMessage
                    defaultMessage="Find the tools, inspiration and resources you need to build a smarter government."
                    id="rdCWnJ"
                  />
                )}
              </Heading>
              <Flex
                display={["none", "none", "flex"]}
                wrap="wrap"
                direction={["column", "column", "column", "row"]}
                align={["center", "center", "center", "unset"]}
              >
                <Box mt={["40px", "40px", "40px", "40px"]} order={[2, 2, 2, 1]}>
                  {!leftButtonHref.startsWith("http") ? (
                    <Link href={leftButtonHref} passHref={true} legacyBehavior={true}>
                      <AnchorButton
                        variant="primary"
                        bg={leftButtonBackgroundColor}
                        color={leftButtonTextColor}
                        borderColor={leftButtonBorderColor}
                        _hover={{
                          bg: leftButtonHoverColor,
                          borderColor: leftButtonHoverColor,
                        }}
                        mr={6}
                        rel="noreferrer noopener"
                        minWidth="250px"
                        whiteSpace="pre-wrap"
                        textAlign="center"
                      >
                        {locale.id === "en-US" ? (
                          leftButtonText
                        ) : (
                          <FormattedMessage defaultMessage="Sign Up" id="39AHJm" />
                        )}
                      </AnchorButton>
                    </Link>
                  ) : (
                    <AnchorButton
                      variant="primary"
                      bg={leftButtonBackgroundColor}
                      color={leftButtonTextColor}
                      borderColor={leftButtonBorderColor}
                      _hover={{
                        bg: leftButtonHoverColor,
                        borderColor: leftButtonHoverColor,
                      }}
                      external="outside-redirect"
                      mr={6}
                      rel="noreferrer noopener"
                      minWidth="250px"
                      whiteSpace="pre-wrap"
                      textAlign="center"
                    >
                      {leftButtonText}
                    </AnchorButton>
                  )}

                  {rightButtonHref.startsWith("http") ? (
                    <AnchorButton
                      variant="primary"
                      external="outside-redirect"
                      href={rightButtonHref}
                      bg={rightButtonBackgroundColor}
                      color={rightButtonTextColor}
                      borderColor={rightButtonBorderColor}
                      _hover={{
                        bg: rightButtonHoverColor,
                        borderColor: rightButtonHoverColor,
                      }}
                      minWidth="250px"
                      whiteSpace="pre-wrap"
                      textAlign="center"
                    >
                      {rightButtonText}
                    </AnchorButton>
                  ) : (
                    <Link href={rightButtonHref} passHref={true} legacyBehavior={true}>
                      <AnchorButton
                        variant="primary"
                        href={rightButtonHref}
                        bg={rightButtonBackgroundColor}
                        color={rightButtonTextColor}
                        borderColor={rightButtonBorderColor}
                        _hover={{
                          bg: rightButtonHoverColor,
                          borderColor: rightButtonHoverColor,
                        }}
                        minWidth="250px"
                        whiteSpace="pre-wrap"
                        textAlign="center"
                      >
                        {locale.id === "en-US" ? (
                          rightButtonText
                        ) : (
                          <FormattedMessage defaultMessage="🎙 Govlaunch Podcast" id="hZP7Ph" />
                        )}
                      </AnchorButton>
                    </Link>
                  )}
                </Box>
              </Flex>
            </Box>

            {bannerContentType === IAboutContentType.Video && bannerContentText && (
              <Box
                zIndex={1}
                mt={[8, 8, 8, 0]}
                ml={[0, 0, 0, "auto"]}
                mx={["auto", "auto", "auto", null]}
                width={["100%", "100%", "100%", "50%"]}
                height={[
                  200,
                  300,
                  bannerContentSize === "small" ? "250px" : "368px",
                  bannerContentSize === "small" ? "250px" : "368px",
                ]}
                position={["relative", "relative", "relative"]}
                right={0}
                top={0}
              >
                <iframe
                  title="vimeo"
                  id="vimeoplayer"
                  width="100%"
                  height="100%"
                  src={`https://player.vimeo.com/video/${getVimeoId(
                    bannerContentText,
                  )}?autoplay=0&loop=0&autopause=0&fullscreen=0`}
                  allow="fullscreen"
                  frameBorder="0"
                />
              </Box>
            )}

            {bannerContentType === IAboutContentType.Podcast && bannerContentText && (
              <Box
                zIndex={1}
                mt={[8, 8, 8, 0]}
                ml={[0, 0, 0, "auto"]}
                mx={["auto", "auto", "auto", null]}
                width={["100%", "100%", "100%", "50%"]}
                // height={[200, 300, "368px", "368px"]}
                position={["relative", "relative", "relative"]}
                dangerouslySetInnerHTML={{
                  __html: bannerContentText,
                }}
              />
            )}

            {bannerContentType === IAboutContentType.Image && bannerContentText && (
              <Image
                ignoreFallback={true}
                zIndex={1}
                mt={[8, 8, 8, 0]}
                ml={[0, 0, 0, "auto"]}
                mx={["auto", "auto", "auto", null]}
                width={["100%", "100%", "550px", "550px"]}
                height={[200, 300, "368px", "368px"]}
                src={bannerContentText}
                alt={bannerContentDescription || ""}
                position={["relative", "relative", "relative"]}
              />
            )}
          </Box>
        </PageGridContainer>
      </Box>

      <Box display={["flex", "flex", "none"]} flexDirection={["column", "column", "row"]} mt={5} px={4} pb={6}>
        <Link href={leftButtonHref} passHref={true} legacyBehavior={true}>
          <AnchorButton
            variant="primary"
            bg={leftButtonBackgroundColor}
            color={leftButtonTextColor}
            borderColor={leftButtonBorderColor}
            _hover={{
              bg: leftButtonHoverColor,
              borderColor: leftButtonHoverColor,
            }}
            mr={6}
            rel="noreferrer noopener"
            href={leftButtonHref}
            fullWidth={true}
            whiteSpace="pre-wrap"
            textAlign="center"
            mb={[4, 4, 0]}
          >
            {locale.id === "en-US" ? leftButtonText : <FormattedMessage defaultMessage="Sign Up" id="39AHJm" />}
          </AnchorButton>
        </Link>

        <AnchorButton
          href={rightButtonHref}
          external="outside-redirect"
          variant="primary"
          bg={rightButtonBackgroundColor}
          color={rightButtonTextColor}
          borderColor={rightButtonBorderColor}
          _hover={{
            bg: leftButtonHoverColor,
            borderColor: leftButtonHoverColor,
          }}
          fullWidth={true}
          whiteSpace="pre-wrap"
          textAlign="center"
        >
          {locale.id === "en-US" ? (
            rightButtonText
          ) : (
            <FormattedMessage defaultMessage="🎙 Govlaunch Podcast" id="hZP7Ph" />
          )}
        </AnchorButton>
      </Box>

      <Box bg={boxesSectionBackgroundColor} pb={[8, 8, "80px"]}>
        <PageGridContainer>
          <SimpleGrid columns={[1, 1, 2]} spacing={4} mt="60px">
            {leftBox.__typename === "TextSideContent" && (
              <InfoBox
                title={
                  locale.id === "en-US"
                    ? leftBox.title
                    : intl.formatMessage({
                        defaultMessage: "Search innovation projects",
                        id: "odMQ4l",
                      })
                }
                titleColor={leftBox.titleFontColor}
                text={
                  locale.id === "en-US"
                    ? leftBox.text
                    : intl.formatMessage({
                        defaultMessage:
                          "Learn how your peers are experimenting with new technology, modern designs and better processes.",
                        id: "wnpufW",
                      })
                }
                textHref={leftBox.textHref}
                textHrefHoverColor={leftBox.textHrefHoverColor}
                textColor={leftBox.textFontColor}
                iconSrc={leftBox.iconSrc || ""}
                width={leftBox.width}
                height={leftBox.height}
                alt={leftBox.iconDescription || ""}
              />
            )}

            {leftBox.__typename === "CarouselSideContent" && (
              <InfoCarousel
                content={leftBox}
                shouldShowButton={
                  !(leftBox.__typename === "CarouselSideContent" && rightBox.__typename === "CarouselSideContent")
                }
              />
            )}

            <Box display={["block", "block", "none"]} height="1px" bg="gray.100" my={4} />

            {rightBox.__typename === "TextSideContent" && (
              <InfoBox
                title={
                  locale.id === "en-US"
                    ? rightBox.title
                    : intl.formatMessage({
                        defaultMessage: "Discover disruptive technologies",
                        id: "YfA6nW",
                      })
                }
                titleColor={rightBox.titleFontColor}
                text={
                  locale.id === "en-US"
                    ? rightBox.text
                    : intl.formatMessage({
                        defaultMessage: "Research tools that can help you improve services and reduce costs.",
                        id: "5nndhW",
                      })
                }
                textHref={rightBox.textHref}
                textHrefHoverColor={rightBox.textHrefHoverColor}
                textColor={rightBox.textFontColor}
                iconSrc={rightBox.iconSrc || ""}
                width={rightBox.width}
                height={rightBox.height}
                alt={rightBox.iconDescription || ""}
              />
            )}

            {rightBox.__typename === "CarouselSideContent" && (
              <InfoCarousel
                content={rightBox}
                shouldShowButton={
                  !(leftBox.__typename === "CarouselSideContent" && rightBox.__typename === "CarouselSideContent")
                }
              />
            )}
          </SimpleGrid>

          {leftBox.__typename === "CarouselSideContent" && rightBox.__typename === "CarouselSideContent" && (
            <DesktopSideButtons leftBox={leftBox} rightBox={rightBox} />
          )}
        </PageGridContainer>
      </Box>

      {(videoSectionTitle || videoSectionContentText) && (
        <Box bg={videoSectionBackground} py={[8, 8, "80px"]}>
          <PageGridContainer>
            {videoSectionTitle && (
              <Heading
                as="h3"
                color={videoSectionFontColor}
                fontWeight="bold"
                textAlign="center"
                fontSize={["2xl", "2xl", "32px"]}
              >
                {locale.id === "en-US" ? videoSectionTitle : <FormattedMessage defaultMessage="..." id="Oo+0sJ" />}
              </Heading>
            )}

            {videoSectionContentText && (
              <Box display="flex" justifyContent="center" mt={videoSectionTitle ? 6 : 0}>
                {videoSectionContentType === IAboutContentType.Video && (
                  <Box width="100%" height={["200px", "200px", "400px"]}>
                    <iframe
                      title="vimeo"
                      id="vimeoplayer"
                      width="100%"
                      height="100%"
                      src={`https://player.vimeo.com/video/${getVimeoId(
                        videoSectionContentText,
                      )}?autoplay=0&loop=0&autopause=0&fullscreen=0`}
                      allow="fullscreen"
                      frameBorder="0"
                    />
                  </Box>
                )}

                {videoSectionContentType === IAboutContentType.Podcast && (
                  <Box
                    width={[350, 350, 720]}
                    dangerouslySetInnerHTML={{
                      __html: videoSectionContentText,
                    }}
                  />
                )}

                {videoSectionContentType === IAboutContentType.Image && (
                  <Image
                    ignoreFallback={true}
                    alt="section image"
                    src={videoSectionContentText}
                    maxWidth={["300px", "400px", "750px"]}
                    objectFit="cover"
                  />
                )}
              </Box>
            )}
          </PageGridContainer>
        </Box>
      )}

      {(joinSectionTitle || joinSectionSubtitle || joinSectionButtonText) && (
        <Box bg={joinSectionBackground} py={[6, 6, "80px", "80px"]} textAlign="center">
          <PageGridContainer display="flex" flexDirection="column" alignItems="center" maxWidth="750px">
            {joinSectionTitle && (
              <Heading as="h3" fontSize={["24px", "24px", "32px", "32px"]} color={joinSectionTitleFontColor}>
                {locale.id === "en-US" ? (
                  joinSectionTitle
                ) : (
                  <FormattedMessage defaultMessage="Government innovation doesn't need to be scary" id="7qeI0e" />
                )}
              </Heading>
            )}

            {joinSectionSubtitle && (
              <Text mt={4} color={joinSectionSubtitleFontColor} fontSize="22px">
                {locale.id === "en-US" ? (
                  joinSectionSubtitle
                ) : (
                  <FormattedMessage
                    defaultMessage="Join our community of local government innovators from the smallest towns to the largest counties – and everything in between."
                    id="ZU8jC+"
                  />
                )}
              </Text>
            )}

            {joinSectionButtonText && joinSectionButtonHref.startsWith("http") && (
              <AnchorButton
                maxWidth="450px"
                variant="solid"
                href={joinSectionButtonHref}
                external="outside-redirect"
                fullWidth={true}
                mt="30px"
                bg={joinSectionButtonColor}
                color={joinSectionButtonFontColor}
              >
                {joinSectionButtonText}
              </AnchorButton>
            )}

            {joinSectionButtonText && !joinSectionButtonHref.startsWith("http") && (
              <Link href={joinSectionButtonHref} passHref={true} legacyBehavior={true}>
                <AnchorButton
                  maxWidth="450px"
                  variant="solid"
                  fullWidth={true}
                  mt="30px"
                  bg={joinSectionButtonColor}
                  color={joinSectionButtonFontColor}
                >
                  {locale.id === "en-US" ? (
                    joinSectionButtonText
                  ) : (
                    <FormattedMessage defaultMessage="Join Govlaunch" id="ljH+sM" />
                  )}
                </AnchorButton>
              </Link>
            )}
          </PageGridContainer>
        </Box>
      )}

      {logoUrls.length > 0 && (
        <Box bg={logoSectionBackgroundColor}>
          <PageGridContainer>
            <Box pb={6}>
              <Stack display="flex" direction="row" flexWrap="wrap" spacing={[6, 6, 10]} justifyContent="center">
                {logoUrls.map((logo, index) => (
                  <Anchor
                    href={getSecureRedirect(logo.url, "outside-govlaunch")}
                    external="govlaunch"
                    key={index}
                    _focus={undefined}
                  >
                    <Image
                      ignoreFallback={true}
                      src={logo.imageUrl}
                      alt={logo.description || ""}
                      mt={6}
                      maxWidth={["140px", "140px", "200px"]}
                      maxHeight={["42px", "42px", "60px"]}
                    />
                  </Anchor>
                ))}
              </Stack>
            </Box>
          </PageGridContainer>
        </Box>
      )}
    </>
  );
}

interface IInfoBoxProps {
  title: string;
  titleColor: string;
  text: string;
  textHref?: string | null | undefined;
  textHrefHoverColor?: string | null | undefined;
  textColor: string;
  iconSrc: string;
  alt: string;
  width: number | null;
  height: number | null;
}

function InfoBox({
  title,
  titleColor,
  text,
  textColor,
  textHref,
  textHrefHoverColor,
  iconSrc,
  width,
  height,
  alt,
}: IInfoBoxProps) {
  return (
    <Box>
      <Flex
        maxWidth={["auto", "auto", "500px"]}
        mx="auto"
        direction="column"
        align="center"
        justify="center"
        textAlign="center"
      >
        <Box mb={[0, 0, 0]}>
          <Image
            ignoreFallback={true}
            src={getResizedIconSrc({ iconSrc, width, height }) || ""}
            alt={alt}
            css={{
              maxHeight: 350,
            }}
          />
        </Box>

        <Heading as="h3" fontSize={["2xl", "2xl", "28px"]} fontWeight="bold" mt={[5, 5, 8]} color={titleColor}>
          {title}
        </Heading>

        {!textHref && (
          <Text fontSize={["md", "md", "22px"]} color={textColor} fontWeight="normal" mt={[3, 3, 4]}>
            {text}
          </Text>
        )}

        {textHref &&
          (textHref.startsWith("http") ? (
            <Anchor external="outside-redirect" href={textHref} color={textColor} whiteSpace="pre-wrap">
              <Text
                fontSize={["md", "md", "22px"]}
                color={textColor}
                fontWeight="normal"
                mt={[3, 3, 4]}
                _hover={{
                  color: textHrefHoverColor || palette.primary,
                }}
              >
                {text}
              </Text>
            </Anchor>
          ) : (
            <Link href={textHref} passHref={true} legacyBehavior={true}>
              <Anchor href={textHref} color={textColor}>
                <Text
                  fontSize={["md", "md", "22px"]}
                  color={textColor}
                  fontWeight="normal"
                  mt={[3, 3, 4]}
                  _hover={{
                    color: textHrefHoverColor || palette.primary,
                  }}
                >
                  {text}
                </Text>
              </Anchor>
            </Link>
          ))}
      </Flex>
    </Box>
  );
}

function getResizedIconSrc(
  content: Pick<
    Extract<
      IAboutPageConfigFieldsFragment["config"]["rightBox"] | IAboutPageConfigFieldsFragment["config"]["leftBox"],
      { __typename: "TextSideContent" }
    >,
    "iconSrc" | "width" | "height"
  >,
) {
  if (!content.width && !content.height) {
    return content.iconSrc;
  }

  if (content.width && !content.height) {
    return `https://imaginary-ssl.govlaunch.com/resize?quality=100&compression=0&width=${content.width}&height=120&url=${content.iconSrc}`;
  }

  if (!content.width && content.height) {
    return `https://imaginary-ssl.govlaunch.com/resize?quality=100&compression=0&height=${content.height}&url=${content.iconSrc}`;
  }

  return `https://imaginary-ssl.govlaunch.com/resize?quality=100&compression=0&width=${content.width}&height=${content.height}&url=${content.iconSrc}`;
}

interface IDesktopSideButtonsProps {
  leftBox: IAboutPageConfigFieldsFragment["config"]["leftBox"];
  rightBox: IAboutPageConfigFieldsFragment["config"]["rightBox"];
}

function DesktopSideButtons({ leftBox, rightBox }: IDesktopSideButtonsProps) {
  return (
    <Box display={["none", "none", "block"]}>
      <SimpleGrid columns={2} spacing={4}>
        <Box display="flex" justifyContent="center">
          {leftBox.__typename === "CarouselSideContent" &&
            leftBox.buttonText &&
            leftBox.buttonHref &&
            leftBox.buttonHref.startsWith("http") && (
              <AnchorButton
                variant="solid"
                href={leftBox.buttonHref}
                external="outside-redirect"
                mx="auto"
                mt="24px"
                bg={leftBox.buttonBackgroundColor}
                color={leftBox.buttonFontColor || "#000"}
              >
                {leftBox.buttonText}
              </AnchorButton>
            )}

          {leftBox.__typename === "CarouselSideContent" &&
            leftBox.buttonText &&
            leftBox.buttonHref &&
            !leftBox.buttonHref.startsWith("http") && (
              <Link href={leftBox.buttonHref} passHref={true} legacyBehavior={true}>
                <AnchorButton
                  variant="solid"
                  mx="auto"
                  mt="24px"
                  bg={leftBox.buttonBackgroundColor}
                  color={leftBox.buttonFontColor || "#000"}
                >
                  {leftBox.buttonText}
                </AnchorButton>
              </Link>
            )}
        </Box>

        <Box display="flex" justifyContent="center">
          {rightBox.__typename === "CarouselSideContent" &&
            rightBox.buttonText &&
            rightBox.buttonHref &&
            rightBox.buttonHref.startsWith("http") && (
              <AnchorButton
                variant="solid"
                href={rightBox.buttonHref}
                external="outside-redirect"
                mx="auto"
                mt="24px"
                bg={rightBox.buttonBackgroundColor}
                color={rightBox.buttonFontColor || "#000"}
              >
                {rightBox.buttonText}
              </AnchorButton>
            )}

          {rightBox.__typename === "CarouselSideContent" &&
            rightBox.buttonText &&
            rightBox.buttonHref &&
            !rightBox.buttonHref.startsWith("http") && (
              <Link href={rightBox.buttonHref} passHref={true} legacyBehavior={true}>
                <AnchorButton
                  variant="solid"
                  mx="auto"
                  mt="24px"
                  bg={rightBox.buttonBackgroundColor}
                  color={rightBox.buttonFontColor || "#000"}
                >
                  {rightBox.buttonText}
                </AnchorButton>
              </Link>
            )}
        </Box>
      </SimpleGrid>
    </Box>
  );
}

interface IInfoCarouselProps {
  content: Extract<
    IAboutPageConfigFieldsFragment["config"]["rightBox"] | IAboutPageConfigFieldsFragment["config"]["leftBox"],
    { __typename: "CarouselSideContent" }
  >;
  shouldShowButton?: boolean;
}

function InfoCarousel({ content, shouldShowButton }: IInfoCarouselProps) {
  return (
    <Box>
      <CarouselProvider
        naturalSlideHeight={0}
        naturalSlideWidth={0}
        totalSlides={content.images.length}
        isIntrinsicHeight={true}
        isPlaying={true}
      >
        <Slider>
          {content.images.map((image, index) => (
            <Slide index={index} key={image.src}>
              <div
                css={{
                  position: "relative",
                }}
              >
                <SlideImage
                  src={image.src}
                  alt={image.alt || ""}
                  hasMasterSpinner={true}
                  css={{
                    height: 350,
                    maxWidth: 375,
                    objectFit: "contain",
                    margin: "0 auto",
                  }}
                />
              </div>
            </Slide>
          ))}
        </Slider>

        <div
          css={{
            display: "flex",
            width: "100%",
            justifyContent: "center",
            minHeight: "28px",
          }}
        >
          {content.images.length > 1 &&
            content.images.map((_, index) => (
              <Dot
                slide={index}
                key={index}
                css={{
                  marginTop: 12,
                  marginRight: 8,
                  borderRadius: "100%",
                  width: 16,
                  height: 16,
                  border: 0,
                  background: "#E3E6EB",
                  "&:disabled": {
                    background: "#96A3B5",
                  },
                }}
              />
            ))}
        </div>
      </CarouselProvider>

      {content.text && !content.textHref && (
        <Text textAlign="center" fontSize="2xl" fontWeight="bold" mt={4} color="gray.900" maxWidth="375px" mx="auto">
          {content.text}
        </Text>
      )}

      {content.text &&
        content.textHref &&
        (content.textHref.startsWith("http") ? (
          <Box display="flex" flexDirection="column" alignItems="center" width="100%">
            <Anchor external="outside-redirect" href={content.textHref} whiteSpace="pre-wrap">
              <Text
                textAlign="center"
                fontSize="2xl"
                fontWeight="bold"
                mt={4}
                color="gray.900"
                maxWidth="375px"
                mx="auto"
                _hover={{
                  color: content.textHrefHoverColor || palette.primary,
                }}
              >
                {content.text}
              </Text>
            </Anchor>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" alignItems="center" width="100%">
            <Link href={content.textHref} passHref={true} legacyBehavior={true}>
              <Anchor href={content.textHref}>
                <Text
                  textAlign="center"
                  fontSize="2xl"
                  fontWeight="bold"
                  mt={4}
                  color="gray.900"
                  maxWidth="375px"
                  mx="auto"
                  _hover={{
                    color: content.textHrefHoverColor || palette.primary,
                  }}
                >
                  {content.text}
                </Text>
              </Anchor>
            </Link>
          </Box>
        ))}

      <Box display={shouldShowButton ? "flex" : ["flex", "flex", "none"]} justifyContent="center">
        {content.buttonText && content.buttonHref && content.buttonHref.startsWith("http") && (
          <AnchorButton
            variant="solid"
            href={content.buttonHref}
            external="outside-redirect"
            mx="auto"
            mt="24px"
            bg={content.buttonBackgroundColor}
            color={content.buttonFontColor || "#000"}
          >
            {content.buttonText}
          </AnchorButton>
        )}

        {content.buttonText && content.buttonHref && !content.buttonHref.startsWith("http") && (
          <Link href={content.buttonHref} passHref={true} legacyBehavior={true}>
            <AnchorButton
              variant="solid"
              mx="auto"
              mt="24px"
              bg={content.buttonBackgroundColor}
              color={content.buttonFontColor || "#000"}
            >
              {content.buttonText}
            </AnchorButton>
          </Link>
        )}
      </Box>
    </Box>
  );
}
